type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  shape: 'rect' | 'circle';
};

const PARTICLE_COUNT = 80;
const DURATION_MS = 2400;
const GRAVITY = 420;
const LAUNCH_SPEED_MIN = 280;
const LAUNCH_SPEED_MAX = 620;

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function createParticles(cx: number, cy: number, colors: string[]): Particle[] {
  const particles: Particle[] = [];
  const fallbackColors = ['#ffb703', '#fb8500', '#0f9d58', '#1a73e8', '#d93025', '#e4c76b'];
  const palette = colors.length >= 2 ? colors : fallbackColors;

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = randomBetween(LAUNCH_SPEED_MIN, LAUNCH_SPEED_MAX);
    particles.push({
      x: cx,
      y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - randomBetween(100, 260),
      color: palette[i % palette.length],
      size: randomBetween(4, 9),
      rotation: Math.random() * 360,
      rotationSpeed: randomBetween(-400, 400),
      opacity: 1,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
    });
  }
  return particles;
}

export function launchConfetti(
  canvas: HTMLCanvasElement,
  colors: string[],
  onComplete?: () => void
): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    onComplete?.();
    return () => {};
  }

  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const particles = createParticles(cx, cy, colors);
  const startTime = performance.now();
  let rafId: number | null = null;
  let destroyed = false;

  function frame(timestamp: number): void {
    if (destroyed) return;

    const elapsed = timestamp - startTime;
    if (elapsed > DURATION_MS) {
      ctx!.clearRect(0, 0, canvas.width, canvas.height);
      onComplete?.();
      return;
    }

    const dt = Math.min(1 / 30, 1 / 60 * 2); // approximate frame dt
    const fadeStart = DURATION_MS * 0.6;

    ctx!.clearRect(0, 0, canvas.width, canvas.height);

    for (const p of particles) {
      p.vy += GRAVITY * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.rotationSpeed * dt;
      p.opacity = elapsed > fadeStart ? Math.max(0, 1 - (elapsed - fadeStart) / (DURATION_MS - fadeStart)) : 1;

      ctx!.save();
      ctx!.globalAlpha = p.opacity;
      ctx!.translate(p.x, p.y);
      ctx!.rotate((p.rotation * Math.PI) / 180);

      ctx!.fillStyle = p.color;
      if (p.shape === 'rect') {
        ctx!.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      } else {
        ctx!.beginPath();
        ctx!.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx!.fill();
      }

      ctx!.restore();
    }

    rafId = requestAnimationFrame(frame);
  }

  rafId = requestAnimationFrame(frame);

  return () => {
    destroyed = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
}
